import {Injectable, signal} from '@angular/core';
import {Observable, of, throwError} from 'rxjs';
import {catchError, delay, finalize, switchMap, tap} from 'rxjs/operators';


@Injectable({
  providedIn: "root"
})
export class LoadingService {

  #loadingSignal= signal<boolean>(false);

  private pendingCount = 0;
  private lastShownAtMs: number | null = null;
  private readonly MIN_VISIBLE_MS = 200;

  loadingSignal = this.#loadingSignal.asReadonly();

  showLoader<T>(obs$: Observable<T>): Observable<T> {
    return of(null)
      .pipe(
        tap(() => this.loadingOn()),
        switchMap(() => obs$),
        tap(() => this.loadingOff()),
        catchError(err => {
          this.loadingOff();
          return throwError(err);
        })
      );
  }

  showLoaderUntilCompleted<T>(obs$: Observable<T>): Observable<T> {
    return of(null)
      .pipe(
        tap(() => this.loadingOn()),
        switchMap(() => obs$),
        delay(100),
        finalize(() => {
          this.loadingOff();
        })
      );
  }


  /*
  *
  * useful for giving the user some visual feedback, in situations where the operation is either too fast or is being performed in the background
  *
  * */

  showLoading(delayMs = 300) {
    return this.showLoader(of(null).pipe(delay(delayMs)));
  }


  loadingOn() {
    this.pendingCount += 1;

    if (this.pendingCount === 1) {
      this.lastShownAtMs = Date.now();
      this.#loadingSignal.set(true);
    }
  }

  loadingOff() {
    this.pendingCount = Math.max(0, this.pendingCount - 1);

    if (this.pendingCount !== 0) {
      return;
    }

    const now = Date.now();
    const shownAt = this.lastShownAtMs ?? now;
    const elapsed = now - shownAt;
    const remaining = Math.max(0, this.MIN_VISIBLE_MS - elapsed);

    setTimeout(() => {
      // Only hide if nothing else started loading in the meantime.
      if (this.pendingCount === 0) {
      this.#loadingSignal.set(false);
      }
    }, Math.max(100, remaining));

  }

}
